import { Injectable, Injector, inject } from '@angular/core';
import LogRocket from 'logrocket';
import { EMPTY, Observable, distinctUntilChanged, filter, map, merge, pairwise, startWith } from 'rxjs';

import { AppConfig } from '@app/app.config';
import { viewReportTokenName } from '@app/config';
import { AuthHelpers, AuthService, AuthTokenActor } from '@shared/services/auth';
import { environment } from 'environments/environment';

type LogRocketNetworkOptions = NonNullable<NonNullable<Parameters<typeof LogRocket.init>[1]>['network']>;
type LogRocketRequest = Parameters<NonNullable<LogRocketNetworkOptions['requestSanitizer']>>[0];
type LogRocketResponse = Parameters<NonNullable<LogRocketNetworkOptions['responseSanitizer']>>[0];

const LOG_ROCKET_APP_ID = 'tca1pn/biscience';
const REDACTED = '[REDACTED]';
const SENSITIVE_HEADERS = ['authorization', viewReportTokenName.toLowerCase()];
const EMAIL = /([\w+-])[\w.+-]*(@|%40)([\w-])[\w-]*(?:\.[\w-]+)*(\.[a-z]{2,})/gi;
const MASKED_EMAIL = '$1***$2$3***$4';
const PHONE = /("[\w-]*phone[\w-]*"\s*:\s*)(?:"(?:[^"\\]|\\.)+"|\d+)/gi;
const TOKEN_ENDPOINT = /\/security\/tokens\//;
// The response is a signature that downloads the export file without authentication.
const DOWNLOAD_SIGNATURE_ENDPOINT = /\/exports\/downloads(?:[?#]|$)/;
// Tracking pixels (Bing UET, LinkedIn Insight) carry the page and referrer urls url-encoded in a query param.
const EMBEDDED_URL = /([?&][^=&#]*=)(https?(?::|%3A)[^&#]*)/gi;
const SENSITIVE_URL_PARTS = [
    /(\/security\/tokens\/[^?#]*\?)[^#]*/gi,
    /([?&](?:jwt|signature|otp)=)[^&#]*/gi,
    /(\/(?:set-password|activate-account)\/)[^/?#]+/gi,
];

@Injectable({
    providedIn: 'root',
})
export class LogRocketService {
    private readonly appConfig = inject(AppConfig);
    // AuthService is resolved lazily: init() runs in an app initializer, before AppConfig is resolved,
    // and constructing AuthService there pulls in services that read AppConfig.appInfo.
    private readonly injector = inject(Injector);

    init(): void {
        if (!environment.production) return;

        LogRocket.init(LOG_ROCKET_APP_ID, {
            network: {
                requestSanitizer: (request) => this.sanitizeRequest(request),
                responseSanitizer: (response) => this.sanitizeResponse(response),
            },
            browser: {
                urlSanitizer: (url) => this.sanitizeUrl(url),
            },
        });
    }

    identify(): Observable<void> {
        if (!environment.production) return EMPTY;

        const authService = this.injector.get(AuthService);
        const loggedIn$ = merge(authService.isAuthenticated().pipe(filter(Boolean)), authService.loggedIn$).pipe(
            map(() => AuthHelpers.decodeToken(authService.authToken)?.actor),
            filter(Boolean),
        );
        const loggedOut$ = authService.loggedOut$.pipe(map((): null => null));

        return merge(loggedIn$, loggedOut$).pipe(
            distinctUntilChanged((previous, current) => previous?.id === current?.id),
            startWith(null),
            pairwise(),
            map(([previous, actor]) => {
                if (previous) LogRocket.startNewSession();

                if (actor) this.identifyActor(actor);
            }),
        );
    }

    private identifyActor({ id, email, firstName, lastName }: AuthTokenActor): void {
        LogRocket.identify(id, {
            email,
            name: `${firstName || ''} ${lastName || ''}`.trim(),
            whiteLabel: this.appConfig.appInfo.id,
        });
    }

    private sanitizeRequest(request: LogRocketRequest): LogRocketRequest {
        const headers = Object.fromEntries(
            Object.entries(request.headers).filter(([name]) => !SENSITIVE_HEADERS.includes(name.toLowerCase())),
        );

        return {
            ...request,
            url: this.sanitizeUrl(request.url),
            headers,
            body: this.sanitizeBody(request.url, request.body),
        };
    }

    private sanitizeResponse(response: LogRocketResponse): LogRocketResponse {
        return {
            ...response,
            url: response.url && this.sanitizeUrl(response.url),
            body:
                response.url && DOWNLOAD_SIGNATURE_ENDPOINT.test(response.url)
                    ? undefined
                    : this.sanitizeBody(response.url, response.body),
        };
    }

    private sanitizeBody(url: string | undefined, body: string | undefined): string | undefined {
        if (url && TOKEN_ENDPOINT.test(url)) {
            return undefined;
        }

        // LogRocket types the body as a string, but non-text payloads (FormData, Blob) come through as-is.
        return typeof body === 'string' ? body.replace(EMAIL, MASKED_EMAIL).replace(PHONE, `$1"${REDACTED}"`) : body;
    }

    private sanitizeUrl(url: string): string {
        return SENSITIVE_URL_PARTS.reduce(
            (sanitized, pattern) => sanitized.replace(pattern, `$1${REDACTED}`),
            url.replace(EMBEDDED_URL, (_, param: string, value: string) => param + this.sanitizeEmbeddedUrl(value)),
        ).replace(EMAIL, MASKED_EMAIL);
    }

    private sanitizeEmbeddedUrl(value: string): string {
        try {
            const url = decodeURIComponent(value);
            const sanitized = this.sanitizeUrl(url);

            return sanitized === url ? value : encodeURIComponent(sanitized);
        } catch {
            return value;
        }
    }
}
